/* Enhance existing controls without replacing native focus, scrolling or navigation. */
(() => {
  const motion = window.YVMotion;
  if (!motion) return;
  document.body.classList.add('motion-ready');
  const signal = new AbortController();
  const observers = [];
  const finePointer = matchMedia('(hover: hover) and (pointer: fine)');

  // Native scroll remains untouched. The line reports the reader's real position.
  const progress = document.createElement('div');
  progress.className = 'reading-progress';
  progress.setAttribute('aria-hidden', 'true');
  document.body.append(progress);
  let scrollFrame = 0;
  const syncScroll = () => {
    scrollFrame = 0;
    const maximum = document.documentElement.scrollHeight - innerHeight;
    const fraction = maximum > 0 ? Math.max(0, Math.min(1, scrollY / maximum)) : 0;
    progress.style.transform = `scaleX(${fraction})`;
    document.body.classList.toggle('has-scrolled', scrollY > 24);
  };
  const requestScroll = () => { if (!scrollFrame) scrollFrame = requestAnimationFrame(syncScroll); };
  addEventListener('scroll', requestScroll, {passive:true, signal:signal.signal});
  addEventListener('resize', requestScroll, {passive:true, signal:signal.signal});
  if ('ResizeObserver' in window) {
    const observer = new ResizeObserver(requestScroll);
    observer.observe(document.body);
    observers.push(observer);
  }
  syncScroll();
  document.querySelectorAll('.yv-mobile-menu a').forEach(link => {
    link.addEventListener('click', () => { link.closest('details').open = false; }, {signal: signal.signal});
  });
  document.querySelectorAll('.yv-mobile-menu').forEach(menu => {
    menu.addEventListener('keydown', event => {
      if (event.key === 'Escape') { menu.open = false; menu.querySelector('summary').focus(); }
    }, {signal: signal.signal});
  });

  // A single indicator slides between controls. No DOM reordering or focus movement.
  document.querySelectorAll('.philosophy-tabs,.hero-view-toggle').forEach(group => {
    const isTabs = group.classList.contains('philosophy-tabs');
    const indicator = document.createElement('span');
    indicator.className = 'selection-indicator';
    indicator.setAttribute('aria-hidden', 'true');
    group.prepend(indicator);
    const update = () => {
      const active = group.querySelector(isTabs ? '[aria-selected="true"]' : '[aria-pressed="true"]');
      if (!active) return;
      indicator.style.width = `${active.offsetWidth}px`;
      indicator.style.height = `${active.offsetHeight}px`;
      indicator.style.transform = `translate3d(${active.offsetLeft}px,${active.offsetTop}px,0)`;
    };
    // Establish the first position before enabling transitions to avoid a load-time slide.
    update();
    requestAnimationFrame(() => group.classList.add('indicator-ready'));
    const observer = new MutationObserver(update);
    observer.observe(group, {subtree:true, attributes:true, attributeFilter:['aria-selected','aria-pressed']});
    observers.push(observer);
    if ('ResizeObserver' in window) {
      const resize = new ResizeObserver(update);
      resize.observe(group);
      observers.push(resize);
    }
  });

  // A gentle pointer-following light lives only on CTAs; images and logos stay untouched.
  document.querySelectorAll('.btn').forEach(button => {
    button.addEventListener('pointermove', event => {
      if (!finePointer.matches || !motion.enabled() || event.pointerType === 'touch') return;
      const bounds = button.getBoundingClientRect();
      button.style.setProperty('--light-x', `${event.clientX - bounds.left}px`);
      button.style.setProperty('--light-y', `${event.clientY - bounds.top}px`);
    }, {passive:true, signal:signal.signal});
  });

  // Text never starts hidden. Browser failures or reduced motion leave readable content.
  if (motion.enabled()) {
    document.querySelectorAll('.hero-copy>.kicker,.hero-copy>h1,.hero-copy>.desc,.hero-copy>.btn,.pdp-info>h1,.pdp-tagline,.page-intro h1')
      .forEach((element,index) => motion.reveal(element, Math.min(index,3) * 70));
    if ('IntersectionObserver' in window) {
      const revealObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          revealObserver.unobserve(entry.target);
          const siblings = [...(entry.target.parentElement?.children || [])];
          const index = Math.max(0, siblings.indexOf(entry.target));
          motion.reveal(entry.target, Math.min(index,3) * 60);
        });
      }, {threshold:.12});
      document.querySelectorAll('main .product-card,main .mini-card,main .section-top,main .summary')
        .forEach(element => revealObserver.observe(element));
      observers.push(revealObserver);
    }
  }

  // Native disclosure semantics, plus a reversible height animation for pointer/keyboard input.
  document.querySelectorAll('.product-disclosure').forEach(details => {
    const summary = details.querySelector('summary');
    const content = details.querySelector('.disclosure-content');
    let animation;
    let expanded = false;
    const settle = () => {
      details.open = expanded;
      details.style.height = '';
      details.style.overflow = '';
    };
    summary.addEventListener('click', event => {
      if (!motion.enabled()) return; // Native toggle handles reduced-motion and capture mode.
      event.preventDefault();
      const from = details.getBoundingClientRect().height;
      animation?.cancel();
      expanded = !expanded;
      details.open = true;
      const collapsed = summary.getBoundingClientRect().height;
      const target = expanded ? collapsed + content.getBoundingClientRect().height : collapsed;
      details.style.overflow = 'hidden';
      details.style.height = `${from}px`;
      animation = motion.animate(details, [{height:`${from}px`},{height:`${target}px`}], {duration:300});
      if (!animation) return settle();
      const current = animation;
      current.finished.then(() => { if (animation === current) settle(); }).catch(() => {
        if (animation === current) settle();
      });
    }, {signal:signal.signal});
    details.addEventListener('toggle', () => {
      if (!animation || animation.playState === 'finished' || animation.playState === 'idle') expanded = details.open;
    }, {signal:signal.signal});
  });

  // A cached page keeps its state and observers; a departing document releases them.
  addEventListener('pagehide', event => {
    if (event.persisted) return;
    signal.abort();
    observers.forEach(observer => observer.disconnect());
    if (scrollFrame) cancelAnimationFrame(scrollFrame);
  }, {once:true});
})();
